import React, { useState, useEffect } from "react";
import {
 fetchVocabularyGroups,
 deleteVocabularyGroup,
} from "./adminVocabularyGroupService";
import VocabularyGroupForm from "./VocabularyGroupForm";
import { useToast } from "../../../common/toast/ToastContext";

const AdminVocabularyGroupPage = () => {
 const { showToast } = useToast();
 const [groups, setGroups] = useState([]);
 const [loading, setLoading] = useState(false);
 const [search, setSearch] = useState("");
 const [page, setPage] = useState(0);
 const [totalPages, setTotalPages] = useState(0);
 const [totalElements, setTotalElements] = useState(0);
 const [showForm, setShowForm] = useState(false);
 const [editingGroup, setEditingGroup] = useState(null);
 const [deleteConfirm, setDeleteConfirm] = useState(null);

 const size = 20;

 useEffect(() => {
 loadGroups();
 }, [page, search]);

 const loadGroups = async () => {
 setLoading(true);
 const params = { page, size };
 if (search.trim()) params.search = search.trim();

 const result = await fetchVocabularyGroups(params);
 if (result.status === 200) {
 setGroups(result.data.content || []);
 setTotalPages(result.data.totalPages || 0);
 setTotalElements(result.data.totalElements || 0);
 } else {
 showToast(result.message || "Failed to load groups", "error");
 }
 setLoading(false);
 };

 const handleSearch = (e) => {
 setSearch(e.target.value);
 setPage(0);
 };

 const handleEdit = (group) => {
 setEditingGroup(group);
 setShowForm(true);
 };

 const handleDelete = async (id) => {
 const result = await deleteVocabularyGroup(id);
 if (result.status === 200) {
 showToast(result.message, "success");
 loadGroups();
 } else {
 showToast(result.message, "error");
 }
 setDeleteConfirm(null);
 };

 const handleFormSuccess = () => {
 setShowForm(false);
 setEditingGroup(null);
 loadGroups();
 };

 const formatDate = (dateString) => {
 return new Date(dateString).toLocaleDateString("en-IN", {
 year: "numeric",
 month: "short",
 day: "numeric",
 });
 };

 return (
 <div className="p-6">
 <div className="flex justify-between items-center mb-6">
 <h1 className="text-2xl font-bold">Vocabulary Groups</h1>
 <button
 onClick={() => setShowForm(true)}
 className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
 >
 Add Group
 </button>
 </div>

 {/* Search */}
 <div className="mb-4">
 <input
 type="text"
 placeholder="Search groups..."
 value={search}
 onChange={handleSearch}
 className="border rounded px-3 py-2 w-64"
 />
 </div>

 {/* Table */}
 <div className="bg-white rounded-lg shadow overflow-hidden">
 <table className="w-full">
 <thead className="bg-gray-50">
 <tr>
 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
 Name
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
 Description
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
 Word Count
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
 Created At
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
 Actions
 </th>
 </tr>
 </thead>
 <tbody className="divide-y divide-gray-200">
 {loading ? (
 <tr>
 <td colSpan="5" className="px-6 py-4 text-center">
 Loading...
 </td>
 </tr>
 ) : groups.length === 0 ? (
 <tr>
 <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
 No groups found
 </td>
 </tr>
 ) : (
 groups.map((group) => (
 <tr key={group.id} className="hover:bg-gray-50">
 <td className="px-6 py-4 font-medium">{group.name}</td>
 <td className="px-6 py-4 text-gray-600">
 {group.description || "-"}
 </td>
 <td className="px-6 py-4">{group.vocabularies?.length || 0}</td>
 <td className="px-6 py-4 text-gray-600">
 {formatDate(group.createdAt)}
 </td>
 <td className="px-6 py-4">
 <button
 onClick={() => handleEdit(group)}
 className="text-blue-600 hover:text-blue-800 mr-3"
 >
 Edit
 </button>
 <button
 onClick={() => setDeleteConfirm(group)}
 className="text-red-600 hover:text-red-800"
 >
 Delete
 </button>
 </td>
 </tr>
 ))
 )}
 </tbody>
 </table>
 </div>

 {/* Pagination */}
 {totalPages > 1 && (
 <div className="flex justify-between items-center mt-4">
 <span className="text-sm text-gray-700">
 Showing {page * size + 1} to {Math.min((page + 1) * size, totalElements)} of {totalElements} results
 </span>
 <div className="flex space-x-2">
 <button
 onClick={() => setPage(page - 1)}
 disabled={page === 0}
 className="px-3 py-1 border rounded disabled:opacity-50"
 >
 Previous
 </button>
 <span className="px-3 py-1">
 Page {page + 1} of {totalPages}
 </span>
 <button
 onClick={() => setPage(page + 1)}
 disabled={page >= totalPages - 1}
 className="px-3 py-1 border rounded disabled:opacity-50"
 >
 Next
 </button>
 </div>
 </div>
 )}

 {/* Form Modal */}
 {showForm && (
 <VocabularyGroupForm
 group={editingGroup}
 onSuccess={handleFormSuccess}
 onCancel={() => {
 setShowForm(false);
 setEditingGroup(null);
 }}
 />
 )}

 {/* Delete Confirmation */}
 {deleteConfirm && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
 <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
 <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
 <p className="text-gray-600 mb-6">
 Are you sure you want to delete "{deleteConfirm.name}"? This action cannot be undone.
 </p>
 <div className="flex justify-end space-x-3">
 <button
 onClick={() => setDeleteConfirm(null)}
 className="px-4 py-2 border rounded hover:bg-gray-50"
 >
 Cancel
 </button>
 <button
 onClick={() => handleDelete(deleteConfirm.id)}
 className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
 >
 Delete
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
};

export default AdminVocabularyGroupPage;